/**
 * `ViewModeCommentButton` — the "Comment on selection" affordance for a
 * READ-ONLY wiki doc (view mode).
 *
 * Why this exists separately from the edit-mode `CommentBubbleMenu`: in
 * view mode the editor is `editable: false`, so ProseMirror does not track
 * a document selection from the reader's gestures — `state.selection` stays
 * empty and TipTap's `BubbleMenu` (which is editable-oriented) never shows.
 * But a comment-only reviewer can ONLY ever be in view mode, so this is the
 * primary inline-comment entry point, not an edge case.
 *
 * It watches the browser's NATIVE selection (`selectionchange`), maps it to
 * a paragraph anchor via `captureAnchorFromDomSelection` (which uses
 * `posAtDOM`), and floats a small button at the selection rect. Clicking it
 * emits the same `wiki:add-inline-comment` event the edit-mode path emits,
 * so the downstream floating-composer flow is identical.
 */

import type { Editor } from '@tiptap/react'
import { MessageCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  type AddInlineCommentDetail,
  captureAnchorFromDomSelection,
  emitAddInlineComment,
} from './inline-comment-extension'

interface ViewModeCommentButtonProps {
  editor: Editor | null
  /** Wiki view-mode + the reader holds `comment-on-docs`. */
  enabled: boolean
}

const BUTTON_OFFSET_PX = 8

export function ViewModeCommentButton({ editor, enabled }: ViewModeCommentButtonProps) {
  const { t } = useTranslation('wiki')
  const [detail, setDetail] = useState<AddInlineCommentDetail | null>(null)

  useEffect(() => {
    if (!enabled || !editor) {
      setDetail(null)
      return
    }

    // `selectionchange` fires rapidly during a drag — debounce so we only
    // recompute once the selection settles.
    let raf = 0
    const recompute = () => {
      window.cancelAnimationFrame(raf)
      raf = window.requestAnimationFrame(() => {
        setDetail(captureAnchorFromDomSelection(editor))
      })
    }

    document.addEventListener('selectionchange', recompute)
    // A scroll moves the selection rect — recompute so the button tracks it
    // (and hides if the selection scrolled out / collapsed).
    window.addEventListener('scroll', recompute, true)
    window.addEventListener('resize', recompute)
    return () => {
      window.cancelAnimationFrame(raf)
      document.removeEventListener('selectionchange', recompute)
      window.removeEventListener('scroll', recompute, true)
      window.removeEventListener('resize', recompute)
    }
  }, [editor, enabled])

  if (!detail) return null

  // Place above the selection start; flip below when there isn't room.
  const buttonH = 34
  const above = detail.rect.top - buttonH - BUTTON_OFFSET_PX
  const placeAbove = above > 8
  const top = placeAbove ? above : detail.rect.bottom + BUTTON_OFFSET_PX
  const left = Math.max(8, Math.min(detail.rect.left, window.innerWidth - 140))

  return createPortal(
    <button
      type="button"
      // mousedown (not click) + preventDefault keeps the native selection
      // alive so the captured anchor + rect stay valid as we emit.
      onMouseDown={(e) => {
        e.preventDefault()
        emitAddInlineComment(detail)
        setDetail(null)
      }}
      aria-label={t('comments.inline.bubbleButtonAria')}
      className="fixed z-50 inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-xs font-medium text-fg shadow-md transition-colors hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      style={{ top, left }}
    >
      <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{t('comments.inline.bubbleButton')}</span>
    </button>,
    document.body,
  )
}
